// The sign-in and key settings shared by the two library screens (SCR-154, amendment 2026-10-09):
// which `sign_in` / `admin_access` fields each screen edits, the "at least one method" rule, the
// body and headers of a write, and the sentences of a refused one. One layered document backs
// both: the org default (`/v1/orgs/{orgId}/installation-settings`) or one installation's override
// (`…/installations/{installationId}/settings`), written whole with the version read (`If-Match`).
// Pure and DOM-free, apart from the two calls at the bottom. Nothing here holds a secret.
import type { ApiClient } from "../../api/client";
import type { ApiError } from "../../api/errors";
import { fieldErrorsFrom, type FieldErrors } from "../../primitives/forms";
import type { HelpId } from "../../primitives/help";
import { getPath, isObject, type Json, type JsonObject } from "../../settings/inherit";
import { writable, writeHeaders } from "../models/model";
import { loadScope as loadLayers } from "../models/models-tab";
import { providersOf, providerToDoc, type ProviderValues } from "./providers";
import { tokenToDoc, type TrustedTokenValues } from "./tokens";

/**
 * The id of the Sign-in editor's first card heading: the proxy's sign-in status links here
 * ("change methods below"), since the editor shares its page.
 */
export const SIGN_IN_EDITOR_ANCHOR = "signin-editor";

/** A card of plain schema fields, rendered by the settings form kit. */
export interface PlainSection {
  title: string;
  help: HelpId;
  paths: readonly string[];
}

/** The Sign-in screen's Cognito sub-tab. */
export const COGNITO_SECTION: PlainSection = {
  title: "Cognito",
  help: "settings.sign_in.cognito",
  paths: ["sign_in.cognito.mode", "sign_in.cognito.user_pool_id", "sign_in.cognito.client_id", "sign_in.cognito.mfa"],
};

/** The Sign-in screen's AWS sign-in sub-tab. */
export const AWS_SIGN_IN_SECTION: PlainSection = {
  title: "AWS sign-in",
  help: "settings.sign_in.aws_sign_in",
  paths: ["sign_in.aws_sign_in.enabled", "sign_in.aws_sign_in.allowed_accounts", "sign_in.aws_sign_in.allowed_principals"],
};

/** The Keys screen's plain cards; the trusted tokens have their own editor below them. */
export const KEY_SECTIONS: readonly PlainSection[] = [
  { title: "API key sign-in", help: "settings.sign_in.api_keys", paths: ["sign_in.api_keys.enabled"] },
  { title: "AWS access keys", help: "settings.sign_in.iam_keys", paths: ["sign_in.iam_keys.enabled", "sign_in.iam_keys.require_mfa"] },
  { title: "Key policy", help: "customers.keyLimit", paths: ["sign_in.key_policy.lifetime_days", "sign_in.key_policy.idle_days", "sign_in.key_policy.max_per_person"] },
];

/**
 * Who is an admin on the proxy: an org-only section (the API refuses it on an installation
 * override), so the card shows at the org default alone, where the draft carries it whole.
 */
export const ADMIN_ACCESS_SECTION: PlainSection = {
  title: "Admin access",
  help: "settings.admin_access",
  paths: ["admin_access.default_admins", "admin_access.teams", "admin_access.match"],
};

/**
 * The fields a card hides until they apply: Cognito's MFA once a pool is on, its pool and app
 * client ids only for an existing pool (a created pool's come from the deployment), and AWS
 * sign-in's lists only while it is enabled. A hidden field keeps its value and blocks no save.
 */
const SHOWN_WHEN: Readonly<Record<string, (document: Json | undefined) => boolean>> = {
  "sign_in.cognito.user_pool_id": (d) => getPath(d, "sign_in.cognito.mode") === "existing",
  "sign_in.cognito.client_id": (d) => getPath(d, "sign_in.cognito.mode") === "existing",
  "sign_in.cognito.mfa": (d) => getPath(d, "sign_in.cognito.mode") !== "disabled",
  "sign_in.aws_sign_in.allowed_accounts": (d) => getPath(d, "sign_in.aws_sign_in.enabled") === true,
  "sign_in.aws_sign_in.allowed_principals": (d) => getPath(d, "sign_in.aws_sign_in.enabled") === true,
};

/** Whether a card shows the field for this document. */
export function isShown(path: string, document: Json | undefined): boolean {
  return SHOWN_WHEN[path]?.(document) ?? true;
}

// ---------------------------------------------------------------------------------- the rules

/** How many methods the document leaves enabled: providers, IAM keys, Cognito, API keys, AWS sign-in. */
export function enabledMethods(document: Json | undefined): number {
  const on = (path: string) => (getPath(document, path) === true ? 1 : 0);
  const providers = providersOf(document).filter((p) => p["enabled"] !== false).length;
  const cognito = getPath(document, "sign_in.cognito.mode");
  return providers + on("sign_in.iam_keys.enabled") + (typeof cognito === "string" && cognito !== "disabled" ? 1 : 0) + on("sign_in.api_keys.enabled") + on("sign_in.aws_sign_in.enabled");
}

export const NO_METHOD_MESSAGE = "At least one sign-in method must stay enabled.";

/** The pointers of `needsRedeploy` that are the Cognito pool's. */
export function cognitoNeedsRedeploy(pointers: readonly string[]): boolean {
  return pointers.some((p) => p === "/sign_in/cognito" || p.startsWith("/sign_in/cognito/"));
}

/** The draft with the providers and tokens the admin edited (null = untouched, so they keep inheriting). */
export function withLists(draft: JsonObject, providers: readonly ProviderValues[] | null, tokens: readonly TrustedTokenValues[] | null): JsonObject {
  if (!providers && !tokens) return draft;
  const signIn: JsonObject = isObject(draft["sign_in"]) ? { ...draft["sign_in"] } : {};
  if (providers) signIn["sso_providers"] = providers.map(providerToDoc);
  if (tokens) signIn["trusted_tokens"] = tokens.map(tokenToDoc);
  return { ...draft, sign_in: signIn };
}

/** A sentence for a refused write that no field explains. */
export function saveFailureMessage(status: number, code: string, message: string): string {
  if (status === 412 || code === "version_conflict") return "These settings changed since you opened them. Reload and make the change again.";
  if (code === "pinned_by_deployment") return "The deployment pins a field you changed; change its IaC parameter instead.";
  if (code === "no_sign_in_method") return NO_METHOD_MESSAGE;
  if (code === "field_staff_only") return "Who serves sign-in is changed by AllCode staff, not here.";
  return message;
}

/** Field errors of a refused save, by the API's dotted paths. */
export function saveFieldErrors(error: ApiError): FieldErrors {
  return fieldErrorsFrom(error);
}

// ----------------------------------------------------------------------------------- the I/O

/** What a screen reads for one scope. */
export interface Loaded {
  /** The document underneath the edited one: absent at the org default. */
  inherited: JsonObject | undefined;
  /** The edited document: the org default, or an installation's override. */
  document: JsonObject;
  version: number;
  pins: Readonly<Record<string, string>>;
  /** `sign_in.served_by` as it applies: the flip's, shown and never sent. */
  servedBy: string;
  needsRedeploy: readonly string[];
}

/** Reads the scope: "" is the org default, otherwise an installation id. */
export async function loadScope(client: ApiClient, orgId: string, scope: string): Promise<Loaded> {
  const read = await loadLayers(client, orgId, scope);
  const applied = read.layers ? (read.layers.effective as unknown as Json) : read.document;
  return {
    inherited: read.inherited,
    document: read.document,
    version: read.version,
    pins: read.pins,
    servedBy: String(getPath(applied, "sign_in.served_by") ?? ""),
    needsRedeploy: read.layers?.needsRedeploy ?? [],
  };
}

/**
 * Writes the scope whole with the version read (`If-Match`), without what the API refuses in a
 * body (`org`, `sign_in.served_by`).
 */
export async function saveScope(client: ApiClient, orgId: string, scope: string, version: number, draft: JsonObject): Promise<void> {
  const headers = writeHeaders(version);
  // The form edits the document as untyped JSON; the generated body types describe the schema's
  // shape (and the partial as an open object), so the body alone is cast here.
  const body = writable(draft);
  if (scope === "") {
    type DefaultBody = Parameters<typeof client.put<"/v1/orgs/{orgId}/installation-settings">>[1]["body"];
    await client.put("/v1/orgs/{orgId}/installation-settings", { path: { orgId }, body: { document: body } as unknown as DefaultBody, headers });
    return;
  }
  type OverrideBody = Parameters<typeof client.put<"/v1/orgs/{orgId}/installations/{installationId}/settings">>[1]["body"];
  await client.put("/v1/orgs/{orgId}/installations/{installationId}/settings", {
    path: { orgId, installationId: scope },
    body: { override: body } as unknown as OverrideBody,
    headers,
  });
}
